import { useCallback, useEffect, useRef, useState } from 'react';
import { machineCameraIssue } from './machine-scan';
import './paper-qr-reader.css';

type Mode = 'idle' | 'opening' | 'camera' | 'image';
export function PaperQrReader({ disabled, onRead }: { disabled: boolean; onRead: (raw: string) => void }) {
  const [mode, setMode] = useState<Mode>('idle'), [error, setError] = useState(''), [message, setMessage] = useState('');
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]), [camera, setCamera] = useState('');
  const video = useRef<HTMLVideoElement>(null), fileInput = useRef<HTMLInputElement>(null);
  const stream = useRef<MediaStream | null>(null), timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const epoch = useRef(0), mounted = useRef(true), handler = useRef(onRead);
  handler.current = onRead;
  const release = useCallback(() => {
    epoch.current++;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    stream.current?.getTracks().forEach(track => track.stop()); stream.current = null;
    if (video.current) video.current.srcObject = null;
  }, []);
  const close = useCallback(() => { release(); if (mounted.current) setMode('idle'); }, [release]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; release(); }; }, [release]);
  useEffect(() => { if (disabled) close(); }, [disabled, close]);
  const accept = (raw: string) => {
    try { handler.current(raw); close(); setError(''); setMessage('Đã đọc được QR trên nhãn. Bạn kiểm tra rồi bấm xác nhận bên dưới.'); return true; }
    catch (e) { setError((e as Error).message); return false; }
  };
  const start = async (deviceId = camera) => {
    release(); const ticket = epoch.current;
    setMode('opening'); setError(''); setMessage('Đang mở camera…');
    try {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) throw Error('Camera streaming not supported');
      // Ask for camera permission directly from the click, before downloading
      // the decoder. Full-resolution pixels do not follow the preview size.
      const opened = await navigator.mediaDevices.getUserMedia({ audio: false, video: {
        ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: 'environment' } }),
        width: { ideal: 1280 }, height: { ideal: 720 },
      } });
      if (!mounted.current || ticket !== epoch.current) { opened.getTracks().forEach(track => track.stop()); return; }
      stream.current = opened;
      const surface = video.current!; surface.srcObject = opened; await surface.play();
      const decoder = await import('./paper-qr');
      if (!mounted.current || ticket !== epoch.current) return;
      const active = opened.getVideoTracks()[0]; setCamera(active?.getSettings().deviceId ?? deviceId);
      void navigator.mediaDevices.enumerateDevices().then(devices => {
        if (mounted.current && ticket === epoch.current) setCameras(devices.filter(device => device.kind === 'videoinput'));
      }).catch(() => {});
      try {
        const focus = active?.getCapabilities?.() as MediaTrackCapabilities & { focusMode?: string[] };
        if (focus?.focusMode?.includes('continuous')) void active.applyConstraints({ advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet] }).catch(() => {});
      } catch { /* Optional focus controls must not prevent QR reading. */ }
      setMode('camera'); setMessage('Đưa QR lại gần camera, giữ nhãn phẳng và tránh ánh sáng phản chiếu.');
      const canvas = document.createElement('canvas'), context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw Error('Không đọc được hình camera. Hãy chọn ảnh nhãn để thử.');
      const began = Date.now();
      const frame = () => {
        if (!mounted.current || ticket !== epoch.current) return;
        try {
          if (surface.readyState >= 2 && surface.videoWidth > 0 && surface.videoHeight > 0) {
            const size = decoder.paperFrameSize(surface.videoWidth, surface.videoHeight);
            canvas.width = size.width; canvas.height = size.height;
            context.drawImage(surface, 0, 0, size.width, size.height);
            const raw = decoder.readPaperPixels(context.getImageData(0, 0, size.width, size.height));
            if (raw && accept(raw)) return;
          }
          if (Date.now() - began > 8000) setMessage('Chưa đọc được mã. Đưa QR chiếm khoảng một phần ba khung hình, giữ yên; hoặc bấm “Chọn ảnh nhãn”.');
        } catch { setMessage('Chưa đọc được hình rõ. Thử chọn camera khác hoặc chọn ảnh chụp nhãn.'); }
        timer.current = setTimeout(frame, 160);
      };
      frame();
    } catch (e) {
      if (!mounted.current || ticket !== epoch.current) return;
      const issue = machineCameraIssue(e);
      close(); setMessage('');
      setError(issue.kind === 'permission'
        ? 'Trình duyệt chưa cho app mở camera. Bấm “Quét QR trên nhãn” để thử lại, rồi chọn “Cho phép” nếu được hỏi. Bạn cũng có thể chọn ảnh nhãn.'
        : issue.message);
    }
  };
  const readImage = async (file?: File) => {
    if (!file) return;
    release(); const ticket = epoch.current; setMode('image'); setError(''); setMessage('Đang đọc QR từ ảnh nhãn…');
    let url = '';
    try {
      if (file.size > 20 * 1024 * 1024) throw Error('Ảnh quá lớn. Chọn ảnh nhãn nhỏ hơn 20 MB.');
      const decoder = await import('./paper-qr');
      url = URL.createObjectURL(file);
      const photo = new Image();
      await new Promise<void>((resolve, reject) => { photo.onload = () => resolve(); photo.onerror = () => reject(Error('Không mở được ảnh. Chọn ảnh JPG hoặc PNG rõ mã QR.')); photo.src = url; });
      if (!mounted.current || ticket !== epoch.current) return;
      const size = decoder.paperFrameSize(photo.naturalWidth, photo.naturalHeight, 2560);
      const canvas = document.createElement('canvas'); canvas.width = size.width; canvas.height = size.height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw Error('Không đọc được ảnh trong trình duyệt này.');
      context.drawImage(photo, 0, 0, size.width, size.height);
      const raw = decoder.readPaperPixels(context.getImageData(0, 0, size.width, size.height));
      if (!raw) throw Error('Chưa đọc được QR trong ảnh. Chụp gần hơn, lấy đủ mã và viền trắng, tránh ảnh mờ hoặc lóa.');
      if (!accept(raw)) setMessage('');
    } catch (e) { if (mounted.current && ticket === epoch.current) { setError((e as Error).message); setMessage(''); } }
    finally { if (url) URL.revokeObjectURL(url); if (mounted.current && ticket === epoch.current) setMode('idle'); }
  };
  return <div className="paper-qr-reader">
    <div className="paper-qr-actions"><button type="button" className="secondary" disabled={disabled || mode === 'image'} onClick={() => { if (mode === 'camera' || mode === 'opening') { close(); setMessage('Đã đóng camera. Bạn có thể mở lại hoặc chọn ảnh nhãn.'); } else void start(); }}>{mode === 'camera' ? 'Đóng camera' : mode === 'opening' ? 'Hủy mở camera' : 'Quét QR trên nhãn'}</button>
      <button type="button" className="secondary" disabled={disabled || mode === 'image'} onClick={() => fileInput.current?.click()}>Chọn ảnh nhãn</button>
      <input ref={fileInput} type="file" accept="image/*" aria-label="Ảnh QR trên nhãn" hidden onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; void readImage(file); }} /></div>
    {cameras.length > 1 && <label>Camera quét mã<select value={camera} disabled={disabled || mode === 'opening' || mode === 'image'} onChange={event => { setCamera(event.target.value); if (mode === 'camera') void start(event.target.value); }}>{cameras.map((device, index) => <option key={device.deviceId} value={device.deviceId}>{device.label || `Camera ${index + 1}`}</option>)}</select></label>}
    <video ref={video} muted playsInline className="paper-qr-preview" hidden={mode !== 'camera' && mode !== 'opening'} aria-label="Hình camera quét QR nhãn" />
    {message && <p role="status">{message}</p>}{error && <p className="print-error" role="alert">{error}</p>}
  </div>;
}
